import { useState } from 'react';
import { Stack } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { PageHeader } from '@liminal/web-ui/display/PageHeader';
import { EmptyState } from '@liminal/web-ui/feedback/EmptyState';
import { Snackbar } from '@liminal/web-ui/feedback/Snackbar';
import { useSnackbar } from '@liminal/web-ui/feedback/useSnackbar';
import { Select } from '@liminal/web-ui/forms/Select';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { NimCall_State, NimCallInitiator } from '@/api/generated';
import { nimCallInitiatorLabel } from '@/components/ui/statusHelpers';
import { getErrorMessage } from '@/errors/messages';
import { useNimCallUsers, useNimCalls } from '@/hooks/useNimCalls';
import { useUrlState } from '@/hooks/useUrlState';
import { NimCallFormDialog } from '../components/NimCallFormDialog';
import { NimCallsTable } from '../components/NimCallsTable';
import { isNimCallsUnavailable } from '../components/nimCalls';

const PAGE_SIZE = 50;

const INITIATORS = Object.values(NimCallInitiator);

const INITIATOR_OPTIONS = [
  { value: '', label: 'Placed by anyone' },
  ...INITIATORS.map((initiator) => ({ value: initiator, label: nimCallInitiatorLabel(initiator) })),
];

const FILTER_DEFAULTS = { initiator: '' };

/**
 * Nim's calls: an operator places one to a user with a brief Nim follows and
 * the user never sees (a research call), and reads how every call went,
 * whoever placed it, with a link to its transcript.
 */
export function NimCallsPage() {
  const [filters, setFilters] = useUrlState(FILTER_DEFAULTS);
  const pagination = useCursorPagination(PAGE_SIZE);
  const [formOpen, setFormOpen] = useState(false);
  const snackbar = useSnackbar();

  const initiator = INITIATORS.find((candidate) => candidate === filters.initiator);
  // A hand-edited link naming no initiator is refused here, not sent.
  const unknownInitiator = filters.initiator !== '' && initiator === undefined;

  const callsQuery = useNimCalls({
    initiator,
    pageSize: PAGE_SIZE,
    pageToken: pagination.pageToken ?? '',
    enabled: !unknownInitiator,
  });
  const calls = callsQuery.data?.calls ?? [];
  const nextPageToken = callsQuery.data?.nextPageToken ?? '';
  const users = useNimCallUsers(calls.map((call) => call.userId));
  const unavailable = callsQuery.isError && isNimCallsUnavailable(callsQuery.error);

  const errorMessage = unknownInitiator
    ? `Calls cannot be filtered by "${filters.initiator}".`
    : callsQuery.isError
      ? `Could not load calls: ${getErrorMessage(callsQuery.error)}`
      : '';

  return (
    <>
      <PageHeader
        title="Calls"
        autoTitleCase={false}
        subtitle="Place a call from Nim to a user, and see how every call with Nim went."
        actions={
          <Button variant="primary" startIcon="call" onClick={() => setFormOpen(true)} disabled={unavailable}>
            Place call
          </Button>
        }
      />

      <Stack spacing={3}>
        {unavailable ? (
          <Card variant="elevated">
            <EmptyState
              icon="call"
              title="Nim calls are not available in this environment"
              subtitle="The voice service does not run here yet, so calls cannot be placed or listed."
              size="sm"
              data-testid="nim-calls-unavailable"
            />
          </Card>
        ) : (
          <>
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
              <Select
                variant="filter"
                aria-label="Placed by"
                size="sm"
                width={220}
                value={filters.initiator}
                onChange={(next) => {
                  setFilters({ initiator: next });
                  pagination.reset();
                }}
                options={INITIATOR_OPTIONS}
              />
            </Stack>

            {/* Newest first, as the server orders them; there is no sort to apply. */}
            <NimCallsTable
              calls={calls}
              users={users.data ?? {}}
              isLoading={callsQuery.isLoading}
              isError={callsQuery.isError || unknownInitiator}
              errorMessage={errorMessage}
              emptyMessage={initiator ? 'No calls placed this way yet.' : 'No calls with Nim yet.'}
            />

            <CursorPagination
              firstRowNumber={pagination.firstRowNumber}
              count={calls.length}
              hasPreviousPage={pagination.hasPreviousPage}
              hasNextPage={!!nextPageToken}
              onPreviousPage={pagination.goToPreviousPage}
              onNextPage={() => pagination.goToNextPage(nextPageToken)}
              disabled={callsQuery.isLoading}
            />
          </>
        )}
      </Stack>

      <NimCallFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onPlaced={({ user, state }) => {
          const who = user.email || user.label;
          if (state === NimCall_State.STATE_MISSED) {
            snackbar.show(`No phone of ${who}'s could be rung. The call is recorded as missed.`, 'error');
          } else {
            snackbar.show(`Ringing ${who}.`);
          }
        }}
      />
      <Snackbar {...snackbar.snackbarProps} />
    </>
  );
}
